/**
 * API data redaction — header values and recursive body field redaction.
 *
 * Separate from redaction.ts (pattern-based string redaction).
 * This module operates on structured data: header name→value pairs
 * and JSON object field names at any nesting depth.
 */

const REDACTED = '[REDACTED]';

/**
 * Redact header values by name (case-insensitive).
 * Returns a new object with matching header values replaced by "[REDACTED]".
 */
export function redactHeaders(
  headers: Record<string, string> | null,
  redactList: readonly string[],
): Record<string, string> | null {
  if (headers === null || redactList.length === 0) return headers;

  const lowerList = new Set(redactList.map((h) => h.toLowerCase()));
  const result: Record<string, string> = {};

  for (const key of Object.keys(headers)) {
    // `Object.hasOwn` is ES2022 and this package pins `lib: ["ES2020"]` on
    // purpose. The prototype call is the same check with no lib requirement.
    if (Object.prototype.hasOwnProperty.call(headers, key)) {
      result[key] = lowerList.has(key.toLowerCase()) ? REDACTED : headers[key];
    }
  }

  return result;
}

/**
 * Redact body field values by name (recursive, any depth).
 * Parses JSON, walks object/array, replaces matching field values with "[REDACTED]".
 * Non-JSON bodies are returned as-is.
 */
export function redactBodyFields(
  body: string | null,
  redactList: readonly string[],
): string | null {
  if (body === null || redactList.length === 0) return body;

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    // Not JSON — return as-is (FR-019)
    return body;
  }

  if (typeof parsed !== 'object' || parsed === null) return body;

  // Field matching is case-insensitive: a config entry of `token` must also
  // catch `Token`, `TOKEN`, `Access_Token`, etc. so casing can't smuggle a
  // secret past the redactor.
  const fieldSet = new Set(redactList.map((f) => f.toLowerCase()));
  const redacted = walkAndRedact(parsed, fieldSet);
  return JSON.stringify(redacted);
}

/**
 * Recursively walk an object/array and redact matching field values.
 * Field names are compared case-insensitively (lowercased).
 */
function walkAndRedact(value: unknown, fields: Set<string>): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => walkAndRedact(item, fields));
  }

  if (typeof value === 'object' && value !== null) {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
      const val = (value as Record<string, unknown>)[key];
      if (fields.has(key.toLowerCase())) {
        result[key] = REDACTED;
      } else {
        result[key] = walkAndRedact(val, fields);
      }
    }
    return result;
  }

  return value;
}

/**
 * Query-string parameters whose values are commonly secrets. Their values are
 * stripped from any URL we store so tokens don't leak via the request URL
 * (which is persisted and rendered verbatim in the report/cloud).
 */
const SECRET_QUERY_PARAMS = new Set(['access_token', 'api_key', 'token', 'sig']);

/**
 * Remove known secret query parameters (access_token, api_key, token, sig)
 * from a URL. Matching is case-insensitive on the parameter name. Returns the
 * original string unchanged when it isn't a parseable absolute URL or when no
 * secret params are present (so URLs aren't needlessly re-serialized).
 */
export function stripSecretQueryParams(rawUrl: string): string {
  if (!rawUrl) return rawUrl;
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    // Not an absolute URL — leave it untouched.
    return rawUrl;
  }
  let changed = false;
  for (const key of [...url.searchParams.keys()]) {
    if (SECRET_QUERY_PARAMS.has(key.toLowerCase())) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  return changed ? url.toString() : rawUrl;
}
